import React, { useState, useEffect, useContext } from 'react'
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Grid,
    Divider,
    Snackbar,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
} from '@mui/material'
import { styled } from '@mui/system'
import axios from 'axios'
import { UserContext } from '../../contexts/UserContext'
import { getBackendUserUrl } from '../../utils/utils'
import ComingSoonPopup from '../../components/common/ComingSoonPopup'
import BillingCardDetails from '../../components/userProfile/BillingCardDetails'
import CompanyMembersList from '../../components/userProfile/CompanyMembersList'

const SidebarButton = styled(Button)(({ theme, active }) => ({
    justifyContent: 'flex-start',
    padding: theme.spacing(1.5, 2),
    textAlign: 'left',
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    fontWeight: active ? 600 : 400,
    fontSize: '1rem',
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
    backgroundColor: active ? theme.palette.action.selected : 'transparent',
    boxShadow: active ? theme.shadows[1] : 'none',
    border: 'none',
    transition: 'background 0.2s, color 0.2s',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.primary.main,
    },
}))

// Add stringToColor utility for avatar backgrounds
const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        color += ('00' + value.toString(16)).substr(-2);
    }
    return color;
}

const ProfileSection = {
    GENERAL: 'General',
    TEAM: 'Team',
    BILLING: 'Billing',
    INTEGRATIONS: 'Integrations',
};

const UserProfilePage = () => {
    const { user, setUser, isAdmin } = useContext(UserContext)
    const [activeSection, setActiveSection] = useState(ProfileSection.GENERAL)
    const [userInfo, setUserInfo] = useState({
        firstName: '',
        lastName: '',
        preferredName: '',
        companyAffiliation: '',
        email: '',
        address: '',
        phone: '',
        type: [], // Initialize as an empty array
    })
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const { showComingSoon } = ComingSoonPopup({})
    const [companyUsers, setCompanyUsers] = useState([]) 
    const [companyLoading, setCompanyLoading] = useState(false)
    const [inviteCopied, setInviteCopied] = useState(false)
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false)
    const [userToRemove, setUserToRemove] = useState(null)
    const [removingUser, setRemovingUser] = useState(false)
    const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false)
    const [lastSavedUserInfo, setLastSavedUserInfo] = useState({});
    const [focusedField, setFocusedField] = useState(null);

    useEffect(() => {
        if (user) {
            const info = {
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                preferredName: user?.preferredName || '',
                companyAffiliation: user?.companyAffiliation || '',
                email: user?.email || '',
                address: user?.address || '',
                phone: user?.phone || '',
                type: Array.isArray(user?.type)
                    ? user.type
                    : [user?.type].filter(Boolean),
            };
            setUserInfo(info);
            setLastSavedUserInfo(info);
        }
        console.log('userInfo here', userInfo)
        console.log('user here', user)
    }, [user])

    // Move fetchCompanyUsers outside useEffect so it can be called after removal
    const fetchCompanyUsers = async () => {
        if (activeSection !== ProfileSection.TEAM || !user?.companyId) return
        setCompanyLoading(true)
        try {
            const token = localStorage.getItem('authToken')
            const response = await axios.get(
                `${getBackendUserUrl()}/company/${user.companyId}/users`,
                { headers: { Authorization: `Bearer ${token}` } }
            )
            setCompanyUsers(response.data || [])
        } catch (err) {
            setCompanyUsers([])
        } finally {
            setCompanyLoading(false)
        }
    }

    useEffect(() => {
        if (activeSection === ProfileSection.TEAM && user?.companyId) {
            fetchCompanyUsers();
        }
        // eslint-disable-next-line
    }, [activeSection, user]);

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setUserInfo((prevState) => ({
            ...prevState,
            [name]: value,
        }))
    }

    const handleBlur = async (e) => {
        const { name, value } = e.target;
        if (lastSavedUserInfo[name] !== value) {
            try {
                const token = localStorage.getItem('authToken');
                const updateData = { [name]: value };
                const apiUrl = getBackendUserUrl() + '/user/update';
                const response = await axios.put(apiUrl, updateData, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.status === 200) {
                    const updatedUser = response.data;
                    setUser(updatedUser);
                    setLastSavedUserInfo((prev) => ({ ...prev, [name]: value }));
                    setShowSuccessSnackbar(false);
                    setTimeout(() => setShowSuccessSnackbar(true), 0);
                } else {
                    throw new Error('Failed to update profile');
                }
            } catch (error) {
                console.error('Error updating profile:', error);
                setError(error.response?.data?.detail || error.message);
            }
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && (!e.shiftKey || !e.target.multiline)) {
            e.preventDefault();
            e.target.blur(); // This will trigger onBlur and save
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault()
        setError('')
        setSuccess('')
        try {
            const token = localStorage.getItem('authToken')
            // Only include fields that have a value
            const updateData = Object.fromEntries(
                Object.entries(userInfo).filter(([_, v]) => {
                    if (Array.isArray(v)) return v.length > 0
                    return v != null && v !== ''
                })
            )
            // Ensure type is an array of strings
            if (updateData.type) {
                updateData.type = Array.from(new Set(updateData.type))
            }
            const apiUrl = getBackendUserUrl() + '/user/update'
            const response = await axios.put(apiUrl, updateData, {
                headers: { Authorization: `Bearer ${token}` },
            })
            if (response.status === 200) {
                const updatedUser = response.data
                setUser(updatedUser)
                localStorage.setItem('userData', JSON.stringify(updatedUser))
                setSuccess('Profile updated successfully!')
                setShowSuccessSnackbar(true)
            } else {
                throw new Error('Failed to update profile')
            }
        } catch (error) {
            console.error('Error updating profile:', error)
            setError(error.response?.data?.detail || error.message)
        }
    }

    const handleRemoveUserRequest = (member) => {
        setUserToRemove(member);
        setRemoveDialogOpen(true);
    };

    const handleConfirmRemoveUser = async () => {
        if (!userToRemove) return;
        setRemovingUser(true);
        try {
            const token = localStorage.getItem('authToken');
            await axios.delete(`${getBackendUserUrl()}/user/remove_user/${userToRemove.userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRemoveDialogOpen(false);
            setUserToRemove(null);
            // Refetch users
            fetchCompanyUsers();
        } catch (err) {
            // Optionally show error
        } finally {
            setRemovingUser(false);
        }
    };

    const handleCancelRemoveUser = () => {
        setRemoveDialogOpen(false);
        setUserToRemove(null);
    };

    const handleFieldFocus = (e) => {
        setFocusedField(e.target.name);
    };

    const handleFieldBlur = (e) => {
        setFocusedField(null);
        handleBlur(e);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: '100vh',
                bgcolor: 'background.default',
            }}
        >
            {/* Sidebar */}
            <Paper
                elevation={3}
                sx={{
                    width: 220,
                    p: 2,
                    display: { xs: 'none', sm: 'block' },
                    bgcolor: 'background.paper',
                    borderRadius: 3,
                    boxShadow: 2,
                    minHeight: 350,
                }}
            >
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                    User Profile
                </Typography>
                <SidebarButton
                    fullWidth
                    active={activeSection === ProfileSection.GENERAL}
                    onClick={() => setActiveSection(ProfileSection.GENERAL)}
                >
                    General
                </SidebarButton>
                <SidebarButton
                    fullWidth
                    active={activeSection === ProfileSection.TEAM}
                    onClick={() => setActiveSection(ProfileSection.TEAM)}
                >
                    Team
                </SidebarButton>
                <SidebarButton
                    fullWidth
                    active={activeSection === ProfileSection.BILLING}
                    onClick={() => setActiveSection(ProfileSection.BILLING)}
                >
                    Billing
                </SidebarButton>
                <SidebarButton
                    fullWidth
                    active={activeSection === ProfileSection.INTEGRATIONS}
                    onClick={showComingSoon}
                >
                    Integrations
                </SidebarButton>
            </Paper>

            {/* Main content */}
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <Typography variant="h4" sx={{ mb: 4 }}>
                    {activeSection === ProfileSection.GENERAL && 'General Information'}
                    {activeSection === ProfileSection.TEAM && 'Team Information'}
                    {activeSection === ProfileSection.BILLING && 'Billing Information'}
                    {activeSection === ProfileSection.INTEGRATIONS && 'Integrations'}
                </Typography>
                {activeSection === ProfileSection.GENERAL && (
                    <Paper elevation={3} sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        name="firstName"
                                        value={userInfo.firstName}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        onKeyDown={handleKeyDown}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#1976d2',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        name="lastName"
                                        value={userInfo.lastName}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        onKeyDown={handleKeyDown}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#1976d2',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Phone"
                                        name="phone"
                                        value={userInfo.phone}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        onKeyDown={handleKeyDown}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#1976d2',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Address"
                                        name="address"
                                        value={userInfo.address}
                                        onChange={handleInputChange}
                                        onBlur={handleBlur}
                                        multiline
                                        rows={2}
                                        onKeyDown={handleKeyDown}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#ccc',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#1976d2',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Tooltip title="These details are managed by your organization. To update your company affiliation or email, please contact our team at team@happenings.dev." arrow>
                                        <TextField
                                            fullWidth
                                            label="Company Affiliation"
                                            name="companyAffiliation"
                                            value={userInfo.companyAffiliation}
                                            disabled={true}
                                            InputProps={{ readOnly: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: '#ccc',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#ccc',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#1976d2',
                                                    },
                                                },
                                            }}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={12}>
                                    <Tooltip title="These details are managed by your organization. To update your company affiliation or email, please contact our team at team@happenings.dev." arrow>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            type="email"
                                            value={userInfo.email}
                                            disabled={true}
                                            InputProps={{ readOnly: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    '& fieldset': {
                                                        borderColor: '#ccc',
                                                    },
                                                    '&:hover fieldset': {
                                                        borderColor: '#ccc',
                                                    },
                                                    '&.Mui-focused fieldset': {
                                                        borderColor: '#1976d2',
                                                    },
                                                },
                                            }}
                                        />
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </form>
                    </Paper>
                )}
                {activeSection === ProfileSection.TEAM && (
                    <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: 'auto', position: 'relative' }}>
                        {/* Header Row: Title and Invite Link */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                Company Members
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                {isAdmin && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        sx={{ borderRadius: 8, fontWeight: 600, boxShadow: 1, textTransform: 'none' }}
                                        onClick={() => {
                                            const inviteLink = `${window.location.origin}/?invite_company_id=${user.companyId}&invited_by=${user.userId}`
                                            navigator.clipboard.writeText(inviteLink)
                                            setInviteCopied(true)
                                        }}
                                    >
                                        Copy Invite Link
                                    </Button>
                                )}
                                {isAdmin && (
                                    <Typography variant="body2" color="text.secondary">
                                        Invite people to join your company
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Snackbar
                            open={inviteCopied}
                            autoHideDuration={2000}
                            onClose={() => setInviteCopied(false)}
                            message="Copied link to clipboard"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            ContentProps={{
                                sx: {
                                    background: theme => theme.palette.grey[900],
                                    color: theme => theme.palette.common.white,
                                    fontWeight: 500,
                                    borderRadius: 2,
                                    boxShadow: 3,
                                    px: 3,
                                    py: 1.5,
                                    fontSize: '1rem',
                                }
                            }}
                        />
                        {companyLoading ? (
                            <Typography>Loading users...</Typography>
                        ) : (
                            <CompanyMembersList 
                                companyUsers={companyUsers}
                                currentUser={user}
                                isAdmin={isAdmin}
                                showComingSoon={showComingSoon}
                                stringToColor={stringToColor}
                                onRemoveUser={handleRemoveUserRequest}
                            />
                        )}
                    </Paper>
                )}
                {activeSection === ProfileSection.BILLING && (
                    <Paper elevation={3} sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Billing
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            You are invited to join a free year of Happenings.
                        </Typography>
                        <BillingCardDetails />
                    </Paper>
                )}
            </Box>
            <Dialog open={removeDialogOpen} onClose={handleCancelRemoveUser}>
                <DialogTitle>Remove User</DialogTitle>
                <DialogContent>
                    Are you sure you want to remove {userToRemove?.firstName} {userToRemove?.lastName} from the company?
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelRemoveUser} disabled={removingUser}>Cancel</Button>
                    <Button onClick={handleConfirmRemoveUser} color="error" disabled={removingUser}>
                        {removingUser ? <CircularProgress size={20} /> : "Remove"}
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={showSuccessSnackbar}
                autoHideDuration={3000}
                onClose={() => setShowSuccessSnackbar(false)}
                message="Profile updated successfully!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Box>
    )
}

export default UserProfilePage
